import uniqueId from 'lodash/uniqueId';

import type {
  FWDChartWidgetV2,
  MarketApp,
  SeasonalChartWidgetV2,
  SimpleChartWidgetV2,
  SpotHistoryTableWidgetV2,
  WidgetV2
} from '@/features/dashboard';
import type {
  BaseTabType,
  CSVTableTabType,
  MarketsCustomChartType,
  MarketsFeedType,
  MarketsGraphType,
  MarketsMinitableType,
  MarketsMultiGroupTableType,
  MarketsSpotHistoryTableType,
  MarketsTableType,
  MiniTableBodyRow,
  MiniTableRow
} from '@/features/markets/types';
import { clock } from '@/services/clock';

const parseToTable = (tab: MarketsTableType) => {
  const sections = tab.sections.map(section => {
    const rows = section.rows.map(row => {
      return {
        shortTitle: row.shortTitle,
        diffSymbol: row.diffSymbol,
        symbol: row.symbol
      };
    });
    return { ...section, rows };
  });
  const app = { ...tab, sections, metadata: tab.metadata ?? null, id: tab.id };

  delete app.tabs;
  return app;
};

const parseToMultiGroupTable = (tab: MarketsMultiGroupTableType) => {
  const sections = tab.sections.map(section => {
    const rows = section.rows.map(row => ({
      shortTitle: row.shortTitle,
      symbolA: row.symbolA,
      symbolAProperties: row.symbolAProperties,
      symbolAMetadata: row.symbolAMetadata,
      symbolB: row.symbolB,
      symbolBProperties: row.symbolBProperties,
      symbolBMetadata: row.symbolBMetadata,
      date: row.date,
      last: row.last,
      delta: row.delta,
      periodAbs: row.periodAbs
    }));
    return { ...section, rows };
  });
  const app = { ...tab, sections, metadata: tab.metadata ?? null, id: tab.id };

  delete app.tabs;
  return app;
};

const parseToMiniTable = (tab: MarketsMinitableType) => {
  const tables = tab.tables.map(table => {
    return {
      ...table,
      rows: table.rows.map(row => {
        const temp = row as unknown as MiniTableRow & MiniTableBodyRow;
        return {
          layout: temp.layout,
          metadata: temp.metadata,
          columns: temp.columns,
          diffSymbol: temp.diffSymbol,
          symbol: temp.symbol,
          from: temp.from
        };
      })
    };
  });

  const app = { ...tab, metadata: tab.metadata ?? null, id: tab.id, tables };

  delete app.tabs;
  return app;
};

const parseToCsvTable = (tab: CSVTableTabType) => {
  const app = { ...tab, metadata: tab.metadata ?? null, id: tab.id };

  delete app.tabs;

  return app;
};
const parseToGraph = (tab: MarketsGraphType | MarketsCustomChartType) => {
  const app = { ...tab, metadata: tab.metadata ?? null, id: tab.id } as
    | SeasonalChartWidgetV2
    | SimpleChartWidgetV2
    | FWDChartWidgetV2;
  delete app.tabs;

  return app;
};

const parseToFeed = (tab: MarketsFeedType) => {
  const app = { ...tab, metadata: tab.metadata ?? null, id: tab.id };
  delete app.tag;
  return app;
};

const parseToSpotHistoryTable = (tab: MarketsSpotHistoryTableType) => {
  if (!tab.items) return undefined;

  const app = { ...tab, metadata: tab.metadata ?? null, id: tab.id } as SpotHistoryTableWidgetV2 & {
    columns?: MarketsSpotHistoryTableType['columns'];
    rows?: MarketsSpotHistoryTableType['rows'];
    tabs?: unknown;
  };
  delete app.columns;
  delete app.rows;
  delete app.tabs;

  return app;
};

const widgetAdapter = {
  newWidget: (tab: BaseTabType): MarketApp | undefined => {
    const widgetType = tab.type;
    let app;
    switch (widgetType) {
      case 'minitable': {
        app = parseToMiniTable(tab);
        break;
      }
      case 'csvtable':
        app = parseToCsvTable(tab);
        break;
      case 'feed': {
        app = parseToFeed(tab);
        break;
      }
      case 'graph':
      case 'graph-custom': {
        app = parseToGraph(tab);
        break;
      }
      case 'table': {
        app = parseToTable(tab);
        break;
      }
      case 'multi_group_table': {
        app = parseToMultiGroupTable(tab);
        break;
      }
      case 'spot_history_table': {
        app = parseToSpotHistoryTable(tab);
        break;
      }
      case 'file_list': {
        app = { ...tab, metadata: tab.metadata ?? null, id: tab.id };
        break;
      }
      default:
        app = undefined;
        break;
    }
    if (!app) {
      return undefined;
    }
    return {
      id: uniqueId(clock.date().toISOString()),
      tabs: [app as WidgetV2]
    };
  }
};

export default widgetAdapter;
